import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useContext,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Button,
  CardMedia,
  IconButton,
  TextField,
  Divider,
  ButtonGroup,
  Chip,
  Table,
  TableRow,
  TableCell,
  Breadcrumbs,
  Link,
  styled,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ProductDetailAccordian from "./ProductDetailAccordian";
import {
  addToCart,
  addToCartGuest,
  fetchProductById,
  getCartDetails,
  updateCartItem,
  addToWishlist,
  getWishListofUser,
  fetchSystemData,
} from "../../services/apiCalls";
import NotFoundPage from "../../components/404";
import { Link as RouterLink } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import useAppDispatch from "../../hooks/useAppDispatch";
import { addItemToCart } from "../../features/cart/cartThunks";
import { useSelector, useDispatch } from "react-redux";
import {
  addWishlistItem,
  fetchWishlist,
  removeWishlistItem,
} from "../../features/wishlist/wishlistThunks";
import { FavoriteOutlined } from "@mui/icons-material";
import { CurrencyContext } from "../../context/CurrencyContext";
import { LanguageContext } from "../../context/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import BreadCrumb from "../../components/BreadCrumb";

const ProductDetailsPage = () => {
  const { selectedCurrency } = useContext(CurrencyContext);
  const { selectedLanguage } = useContext(LanguageContext);
  const [product, setproduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedSpecification, setSelectedSpecification] = useState("");
  const [loading, setLoading] = useState(true);
  const { productId } = useParams();
  const [selectedVariations, setSelectedVariations] = useState({});
  const [selectedCombination, setSelectedCombination] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [wishlistMessage, setWishlistMessage] = useState("");
  const [isBuyingNow, setIsBuyingNow] = useState(false);
  const translate = useTranslation();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();
  const [systemData, setSystemData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    logo: "",
    fbLink: "",
    instaLink: "",
  });
  const dispatch = useAppDispatch();
  const { items: wishlistItems, loading: wishlistLoading } = useSelector(
    (state) => state.wishlist
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchSystemData();
        setSystemData({
          name: data?.name || "Company Name",
          address: data.address,
          phone: data?.phone || "+1 (234) 567-890",
          email: data?.email || "info@example.com",
          logo: data?.logo,
          fbLink: data?.fb_link,
          instaLink: data?.inst_link,
        });
      } catch (error) {
        console.error("Failed to fetch system data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    console.log(JSON.stringify(selectedCombination) + "selectedCombination");

    return () => { };
  }, [selectedCombination]);

  useEffect(() => {
    const fetchGetProductDetails = async () => {
      setLoading(true);
      try {
        const response = await fetchProductById(productId);
        setproduct(response.data);

        const variationImages = response.data.product_variation_combination
          .map((combination) => combination.image)
          .flat();

        setproduct((prevProduct) => ({
          ...prevProduct,
          images: [...prevProduct.images, ...variationImages],
        }));

        if (response.data.product_variation_tempalte.length > 0) {
          const initialVariations = {};
          response.data.product_variation_tempalte.forEach((variation) => {
            if (variation.option.length > 0) {
              const firstOption = variation.option[0];
              const matchingCombination =
                response.data.product_variation_combination.find(
                  (combination) =>
                    combination.attributes[variation.name] === firstOption.name
                );

              if (matchingCombination && matchingCombination.stock > 0) {
                initialVariations[variation.name] = firstOption.name;
              }

              if (matchingCombination) {
                setSelectedCombination({
                  id: matchingCombination.attribute_id,
                  price: matchingCombination.price || product?.sales_price,
                  stock: matchingCombination.stock,
                  sku: matchingCombination.sku,
                });
              }
            }
          });
          setSelectedVariations(initialVariations);
        }

        if (response.data.images.length > 0) {
          setSelectedImage(response.data.images[0]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGetProductDetails();
  }, [productId]);

  useEffect(() => {
    dispatch(fetchWishlist());
  }, [dispatch]);

  const isInWishlist = useMemo(() => {
    return product && wishlistItems.some((item) => item.id === product.id);
  }, [product, wishlistItems]);

  const handleAddToWishlist = async () => {
    if (isInWishlist) {
      setSnackbar({
        open: true,
        message: "This product is already in your wishlist!",
        severity: "warning",
      });
      return;
    }

    try {
      await dispatch(addWishlistItem(product.id)).unwrap();
      setSnackbar({
        open: true,
        message: "Product added to wishlist successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to add product to wishlist",
        severity: "error",
      });
    }
  };

  const calculateTotalPrice = useCallback(() => {
    if (!product || !selectedVariations) return 0;

    let basePrice = 0;

    if (product.product_variation_tempalte.length > 0) {
      product.product_variation_tempalte.forEach((variation) => {
        const selectedOption = variation.option.find(
          (opt) => opt.name === selectedVariations[variation.name]
        );
        if (selectedOption?.value) {
          basePrice += parseFloat(selectedOption.value);
        } else {
        }
      });
    } else {
      basePrice = product.sales_price;
    }

    if (selectedCurrency.code === "Rs") {
      return basePrice * quantity;
    } else {
      return (basePrice / parseFloat(selectedCurrency.ratio)) * quantity;
    }
  }, [
    product,
    selectedVariations,
    selectedCurrency.code,
    selectedCurrency.ratio,
    quantity,
  ]);

  const totalPrice = calculateTotalPrice();

  const getStockForSelection = useCallback(() => {
    if (!product) return 0;
    if (product.product_variation_combination?.length) {
      const matchingCombination = product.product_variation_combination.find(
        (combination) =>
          JSON.stringify(combination.attributes) ===
          JSON.stringify(selectedVariations)
      );
      return matchingCombination ? matchingCombination.stock : 0;
    }
    if (product.is_single_product && product.total_stock) {
      return parseInt(product.total_stock, 10);
    }
    return 0;
  }, [selectedVariations, product]);

  const availableStock = getStockForSelection();

  const isVariationEnabled = (variationIndex) => {
    const priorVariations = product.product_variation_tempalte.slice(
      0,
      variationIndex
    );

    return priorVariations.every(
      (priorVariation) => selectedVariations[priorVariation.name] !== undefined
    );
  };

  const isOptionValid = useCallback(
    (variationName, optionName) => {
      const tempSelectedVariations = {
        ...selectedVariations,
        [variationName]: optionName,
      };

      return product.product_variation_combination.some((combination) =>
        Object.entries(tempSelectedVariations).every(
          ([key, value]) => combination.attributes[key] === value
        )
      );
    },
    [product, selectedVariations]
  );

  const findFirstAvailableOption = useCallback(
    (variation) => {
      const optionWithStock = variation.option.find((option) => {
        return product.product_variation_combination.some(
          (combination) =>
            combination.stock > 0 &&
            combination.attributes[variation.name] === option.name
        );
      });

      return optionWithStock || variation.option[0];
    },
    [product]
  );

  useEffect(() => {
    if (product?.product_variation_tempalte?.length > 0) {
      const initialVariations = {};

      product.product_variation_tempalte.forEach((variation) => {
        const firstOption = findFirstAvailableOption(variation);
        if (firstOption) {
          initialVariations[variation.name] = firstOption.name;
        }
      });

      const matchingCombination = product.product_variation_combination.find(
        (combination) =>
          Object.entries(initialVariations).every(
            ([key, value]) => combination.attributes[key] === value
          )
      );

      if (matchingCombination) {
        setSelectedCombination({
          id: matchingCombination.attribute_id,
          price: matchingCombination.price || product?.sales_price,
          stock: matchingCombination.stock,
          sku: matchingCombination.sku,
        });
      }

      setSelectedVariations(initialVariations);
    }
  }, [product, findFirstAvailableOption]);

  const handleSelectVariation = (variationName, selectedOption) => {
    setSelectedVariations((prev) => ({
      ...prev,
      [variationName]: selectedOption.name,
    }));
    const allVariations = product.product_variation_tempalte.reduce(
      (acc, variation) => {
        const optionForVariation =
          variation.name === variationName
            ? selectedOption.name
            : selectedVariations[variation.name];
        return {
          ...acc,
          [variation.name]: optionForVariation,
        };
      },
      {}
    );
    const matchingCombination = product.product_variation_combination.find(
      (combination) => {
        const isMatching = Object.entries(allVariations).every(
          ([key, value]) => combination.attributes[key] === value
        );
        return isMatching;
      }
    );
    if (matchingCombination) {
      setSelectedCombination({
        id: matchingCombination.attribute_id,
        price: matchingCombination.price || product?.sales_price,
        stock: matchingCombination.stock,
        sku: matchingCombination.sku,
      });
    } else {
      setSelectedCombination({
        id: null,
        price: product?.sales_price,
        stock: 0,
        sku: null,
      });
    }
  };

  const handleIncrement = () => {
    setQuantity((prev) => Math.min(prev + 1, availableStock));
  };

  const handleDecrement = () => {
    setQuantity((prev) => Math.max(prev - 1, 1));
  };

  const getAuthToken = () => {
    const userStr = localStorage.getItem("user");
    if (!userStr) {
      throw new Error("User data is missing from storage");
    }

    try {
      const userData = JSON.parse(userStr);
      if (!userData.token) {
        throw new Error("Authentication token is missing");
      }
      return userData.token;
    } catch (error) {
      console.error("Error parsing user data:", error);
      throw new Error("Invalid user data in storage");
    }
  };

  const handleAddToCart = async () => {
    try {
      setIsAddingToCart(true);

      const cartResponse = await getCartDetails();
      const cartItems = cartResponse.data || [];

      // dispatch(addItemSuccess(cartResponse.data));

      const isSingleProduct =
        !product.product_variation_combination ||
        product.product_variation_combination.length === 0;

      let existingItem;
      if (isSingleProduct) {
        existingItem = cartItems.find((item) => item.product.id === product.id);
      } else {
        existingItem = cartItems.find(
          (item) =>
            item.product.id === product.id &&
            item.variant_id === selectedCombination.id
        );
      }

      if (existingItem) {
        const newQuantity = parseFloat(existingItem.quantity) + quantity;

        if (newQuantity > existingItem.stock) {
          setSnackbarSeverity("warning");
          setSnackbarMessage("Cannot exceed available stock");
          setSnackbarOpen(true);
          setIsAddingToCart(false);
          return;
        }

        await updateCartItem({
          card_id: existingItem.card_id,
          quantity: newQuantity?.toString(),
          discount: existingItem.discount,
        });

        setSnackbarSeverity("success");
        setSnackbarMessage(
          isSingleProduct
            ? "Quantity updated for the single product."
            : "Quantity updated for the selected combination."
        );
        setSnackbarOpen(true);
      } else {
        const cartItem = isSingleProduct
          ? {
            product_id: product.id,
            discount: product.discount || "",
            quantity: quantity?.toString(),
            line_discount_type: "percentage",
            variant_id: null, // No variant for single product
            unit_price: product.sales_price?.toString(),
          }
          : {
            product_id: product.id,
            discount: product.discount || "",
            quantity: quantity?.toString(),
            line_discount_type: "percentage",
            variant_id: selectedCombination.id,
            unit_price: selectedCombination?.price?.toString(),
          };

        dispatch(addItemToCart(cartItem));

        // setSnackbarSeverity("success");
        // setSnackbarMessage("Added to cart successfully");
        // setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Failed to add to cart:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to add to cart");
      setSnackbarOpen(true);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    try {
      setIsBuyingNow(true);

      // Check stock
      if (availableStock <= 0) {
        setSnackbarMessage("Sorry, this item is out of stock");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      if (product.product_variation_tempalte?.length > 0) {
        const allVariationsSelected =
          Object.keys(selectedVariations).length ===
          product.product_variation_tempalte.length;

        if (!allVariationsSelected) {
          setSnackbarMessage("Please select all variations");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
          return;
        }
      }

      const cartResponse = await getCartDetails();
      const cartItems = cartResponse.data || [];
      const existingItem = cartItems.find(
        (item) => item.product.id === product.id
      );

      if (existingItem) {
        const newQuantity = parseFloat(existingItem.quantity) + quantity;
        if (newQuantity > availableStock) {
          setSnackbarMessage("Cannot exceed available stock");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
          return;
        }

        await updateCartItem({
          card_id: existingItem.card_id,
          quantity: newQuantity.toString(),
          discount: existingItem.discount,
        });
      } else {
        const cartItem = {
          product_id: product.id,
          discount: product.discount || "",
          quantity: quantity.toString(),
          line_discount_type: "percentage",
          unit_price:
            selectedCombination?.price || product.sales_price.toString(),
          variant_id: selectedCombination?.id || "",
        };

        const userStr = localStorage.getItem("user");
        if (userStr) {
          await addToCart(getAuthToken(), cartItem);
        } else {
          await addToCartGuest(cartItem);
        }
      }

      // Navigate to checkout
      navigate("/checkout");
    } catch (error) {
      setSnackbarMessage("Failed to process your order");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsBuyingNow(false);
    }
  };

  const handleWishlistAction = async () => {
    const user = localStorage.getItem("user");

    if (!user) {
      navigate("/signin");
      return;
    }

    try {
      if (isInWishlist) {
        const wishlistItem = wishlistItems.find(
          (item) => item.id === product.id
        );
        await dispatch(removeWishlistItem(wishlistItem.wishlist_id)).unwrap();
        setSnackbar({
          open: true,
          message: "Product removed from wishlist!",
          severity: "success",
        });
      } else {
        await dispatch(addWishlistItem(product.id)).unwrap();
        setSnackbar({
          open: true,
          message: "Product added to wishlist successfully!",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to update wishlist",
        severity: "error",
      });
    }
  };

  const breadcrumbUrls = [
    // { path: '/previous', label: 'Back' },
    { path: '/', label: 'Home' },
    { path: '/products/1', label: 'Products' },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <CircularProgress />
        <Typography variant="h6" mt={2}>
          Loading Product Details...
        </Typography>
      </Box>
    );
  }

  return product ? (
    <Box>

      <Box sx={{
        backgroundImage: `url('/laptop.jpg')`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        padding: 2,
        borderRadius: 0,
        display: "flex", // Enables flexbox
        justifyContent: "center", // Centers horizontally
        alignItems: "center", // Centers vertically
        height: "250px", // Adjust height as needed for proper centering
      }}
      >
        {/* <Breadcrumbs
          aria-label="breadcrumb"
          separator=""
          sx={{
            display: "inline-flex",
            opacity: 0.9,
            // backgroundColor: "rgba(255, 255, 255, 0.8)",
            padding: "8px 16px",
            borderRadius: 1,
          }}
        >
          <Link
            underline="hover"
            color="inherit"
            href="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "16px",
            }}
          >
            Home
          </Link>

          <Link
            underline="hover"
            color="inherit"
            to={`/products/${product.product_category.id}`}
            component={RouterLink}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "16px",
            }}
          >
            <Box
              sx={{
                width: "5px",
                height: "5px",
                backgroundColor: "#000",
                transform: "rotate(45deg)",
              }}
            />
            Products
          </Link>
          <Link
            underline="none"
            color="inherit"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "16px",
            }}
          >
            <Box
              sx={{
                width: "5px",
                height: "5px",
                backgroundColor: "#000",
                transform: "rotate(45deg)",
              }}
            />
            {product?.name.length > 70
              ? product?.name.slice(0, 70) + "..."
              : product?.name}
          </Link>
        </Breadcrumbs> */}
        <BreadCrumb title="Product" urls={breadcrumbUrls} />
      </Box>
      <Box
        padding={{ xs: 1, md: 4 }}
        sx={{
          width: { xs: "100%", md: "90%", lg: "100%" },
          marginX: "auto",
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={4} lg={6}>
            <Box display="flex" alignItems="flex-start" sx={{ padding: 0, margin: 0 }}>
              <Box
                display="flex"
                flexDirection="column"
                gap={1}
                sx={{ marginRight: 4, marginLeft: 0 }}
              >
                {product.images.map((image, index) => (
                  <CardMedia
                    key={index}
                    component="img"
                    image={image}
                    alt={`Thumbnail ${index + 1}`}
                    sx={{
                      width: 60,
                      height: 60,
                      // borderRadius: 1,
                      cursor: "pointer",
                      border:
                        selectedImage === image
                          ? "2px solid #1976d2"
                          : "1px solid gray",
                    }}
                    onClick={() => setSelectedImage(image)}
                  />
                ))}
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                flex="1"
              >
                <CardMedia
                  component="img"
                  image={selectedImage || product.images[0]}
                  alt="Selected Product"
                  sx={{ width: "100%", height: "auto" }}
                  onError={(e) => {
                    console.log(
                      "Current image:",
                      selectedImage || product.images[0]
                    );
                    console.log("All images:", product.images);
                  }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={8} lg={6}>
            <Typography
              variant="h4"
              fontWeight={400}
              fontSize={"36px"}
              gutterBottom
            >
              {translate(product.name)}
            </Typography>

            <Divider sx={{ my: 2, borderBottomWidth: '2px' }} />

            <Box display="flex" alignItems="center" gap={2}>
              {product.discount ? (
                <>

                  <Typography
                    variant="p"
                    sx={{
                      color: "#757575",
                      textDecoration: "line-through",
                    }}
                    gutterBottom
                  >
                    {selectedCurrency.code} {totalPrice}
                  </Typography>
                  <Typography
                    variant="p"
                    // fontWeight="bold"
                    // sx={{ color: "#2189ff" }}
                    gutterBottom
                  >
                    {selectedCurrency.code}{" "}
                    {(totalPrice * (1 - product.discount / 100)).toFixed(2)}
                  </Typography>
                  <Chip
                    label={`${product.discount}% OFF`}
                    sx={{
                      backgroundColor: "#9e6747",
                      borderRadius: 0,
                      color: "#ffffff",
                      fontWeight: 500,
                      fontSize: "1rem",
                      height: "32px",
                      padding: "0 12px",
                      "& .MuiChip-label": {
                        padding: "0 8px",
                      },
                    }}
                  />
                </>
              ) : (
                <Typography
                  variant="p"
                  // sx={{ color: "#2189ff" }}
                  // fontWeight="bold"
                  gutterBottom
                >
                  {selectedCurrency.code} {totalPrice.toFixed(2) || 0}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2, borderBottomWidth: '2px' }} />

            {product.product_variation_tempalte?.length > 0 && (
              <Box sx={{ marginTop: "16px" }}>
                {product.product_variation_tempalte.map((variation, index) => (
                  <Box key={variation.name} sx={{ marginBottom: "32px" }}>
                    <Grid container>
                      <Typography
                        variant="subtitle1"
                        fontWeight="500"
                        gutterBottom
                        pr={2}
                      >
                        {variation.name}:
                      </Typography>
                      {variation.option.map((option) => {
                        const isSelected =
                          selectedVariations[variation.name] === option.name;
                        const isValid = isOptionValid(
                          variation.name,
                          option.name
                        );
                        const isEnabled = isVariationEnabled(index);
                        return (
                          <Grid item key={option.name}>
                            <Chip
                              label={`${option.name}`}
                              sx={{
                                backgroundColor: isSelected
                                  ? "#2189ff"
                                  : isValid && isEnabled
                                    ? "#e0e0e0"
                                    : "#f5f5f5",
                                color: isSelected
                                  ? "#fff"
                                  : isValid && isEnabled
                                    ? "#000"
                                    : "#999",
                                "&:hover":
                                  isValid && isEnabled
                                    ? {
                                      backgroundColor: "#2189ff",
                                      color: "#fff",
                                    }
                                    : undefined,
                              }}
                              onClick={() =>
                                isValid &&
                                isEnabled &&
                                handleSelectVariation(variation.name, option)
                              }
                              disabled={!isValid || !isEnabled}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}



            {/* {product.discount ? (
              <Typography
                mb={"32px"}
                variant="body1"
                color="subtitle1"
                sx={{ marginTop: "16px", fontWeight: "500" }}
              >
                Total Price: {selectedCurrency.code}{" "}
                {(totalPrice * (1 - product.discount / 100)).toFixed(2)}
              </Typography>
            ) : (
              <Typography
                mb={"32px"}
                variant="body1"
                color="subtitle1"
                sx={{ marginTop: "16px", fontWeight: "500" }}
              >
                Total Price: {selectedCurrency.code} {totalPrice.toFixed(2)}
              </Typography>
            )} */}

            <Grid item xs={12} md={8} lg={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {product.product_brand && (
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: "500", fontFamily: '"Playfair Display", serif' }}
                    >
                      Brand: {translate(product.product_brand.name)}
                    </Typography>
                  </Box>
                )}
                {product.warranty && (
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: "500", textAlign: "right", fontFamily: '"Playfair Display", serif' }}
                    >
                      Warranty: {translate(product.warranty.name)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            {(product.warranty || product.product_brand) && (
              <Divider sx={{ my: 2, borderBottomWidth: '2px' }} />
            )}



            <Box sx={{ mb: 2, display: "flex" }}>
              <Typography
                variant="body1"
                backgroundColor={availableStock > 0 ? "#337239" : "red"}
                color={"#ffffff"}
                sx={{
                  fontWeight: "500",
                  fontFamily: '"Playfair Display", serif'
                }}
                paddingLeft={"15px"}
                paddingRight={"15px"}
                padding={"5px"}
              >
                {availableStock > 0
                  ? `${availableStock} in Stock `
                  : "Out of Stock"}
              </Typography>
            </Box>

            <Box
              sx={{
                // mb: "32px",
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
              }}
            >
              {/* <Box sx={{}}>
                <Typography variant="subtitle1" fontWeight="500" pr={2}>
                  Quantity:
                </Typography>
              </Box> */}

            </Box>

            <Grid
              container
              rowGap={2}
              columnSpacing={{ xs: 0, md: 2 }}
              width={{ xs: "100%", md: "600px" }}
            >
              <Grid item xs={12} md={3}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    // borderRadius: "50px",
                    border: "1px solid #000000",
                    // backgroundColor: "#f5f5f5",
                    overflow: "hidden",
                    // width: "100px",
                    height: "100%"
                  }}>
                  <IconButton
                    size="small"
                    onClick={handleDecrement}
                    disabled={quantity === 1}
                    sx={{
                      color: "#000",
                      p: 1,
                      width: "35%",
                      borderRadius: 0,
                      "&:hover": {
                        backgroundColor: "#e0e0e0",
                      },
                    }}
                  >
                    <RemoveIcon fontSize="small" sx={{ fontWeight: 200 }} />
                  </IconButton>
                  <Typography
                    variant="body1"
                    sx={{
                      mx: 0,
                      textAlign: "center",
                      flexGrow: 1,
                      fontSize: "1.25em",
                    }}
                  >
                    {availableStock === 0 ? 0 : quantity}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleIncrement}
                    disabled={quantity === availableStock || availableStock === 0}
                    sx={{
                      color: "#000",
                      p: 1,
                      width: "35%",
                      borderRadius: 0,
                      "&:hover": {
                        backgroundColor: "#e0e0e0",
                      },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>



                </Box>



              </Grid>
              <Grid item xs={12} md={4}
              //  sx={{height:"10px"}}
              >
                <LoadingButton
                  loading={isAddingToCart}
                  disabled={isAddingToCart || availableStock === 0}
                  variant="contained"
                  onClick={handleAddToCart}
                  sx={{
                    width: "100%",
                    fontFamily: '"Playfair Display", serif'
                  }}
                  startIcon={<ShoppingCartIcon />}
                >
                  {isAddingToCart ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Add to Cart"
                  )}
                </LoadingButton>
              </Grid>
              <Grid item xs={12} md={5}>
                <Button
                  variant="contained"
                  color={isInWishlist ? "error" : "bluebutton"}
                  startIcon={
                    isInWishlist ? <FavoriteOutlined /> : <FavoriteBorderIcon />
                  }
                  onClick={handleWishlistAction}
                  fullWidth
                  sx={{
                    flexGrow: 1,
                    fontFamily: '"Playfair Display", serif'
                  }}
                >
                  {isInWishlist ? "Remove Wishlist" : "Add to Wishlist"}
                </Button>
              </Grid>
              <br />
              <Grid item xs={12} md={3} sx={{

              }}>
                <Button
                  variant="contained"
                  color="bluebutton"
                  fullWidth
                  // sx={{ py: 1, mb: 2 }}
                  disabled={availableStock === 0 || isBuyingNow}
                  onClick={handleBuyNow}
                  sx={{ fontFamily: '"Playfair Display", serif' }}
                >
                  {isBuyingNow ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Buy Now"
                  )}
                </Button>
              </Grid>


            </Grid>


            {/* <Typography color="text.secondary" sx={{ mb: 2 }}>
              <LocalShippingIcon /> Estimated Delivery:{" "}
              {product.estimatedDelivery}
            </Typography> */}

            <Box display="flex" gap={1} alignItems={"center"} sx={{ mt: 2 }}>
              <Typography color="text.black" sx={{ fontFamily: '"Playfair Display", serif' }}>Share :</Typography>
              {systemData.fbLink && (
                <IconButton
                  color="blackbutton"
                  aria-label="Facebook"
                  href={systemData.fbLink}
                  target="_blank"
                >
                  <FacebookIcon />
                </IconButton>
              )}
              {systemData.instaLink && (
                <IconButton
                  color="blackbutton"
                  aria-label="Facebook"
                  href={systemData.fbLink}
                  target="_blank"
                >
                  <InstagramIcon />
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>

        <Box mt={2} display={"flex"} flexDirection={"column"} gap={2}>
          {/* <ProductDetailAccordian title={"Specification"}>
            {product.online_description && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  dangerouslySetInnerHTML={{
                    __html: product.online_description,
                  }}
                />
              </Box>
            )}
          </ProductDetailAccordian> */}

          {product.shipping_info && (
            <ProductDetailAccordian title={"Shipping Information"}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ fontFamily: '"Playfair Display", serif' }}>
                Returns Policy
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontFamily: '"Playfair Display", serif' }}>
                You may return most new, unopened items within 30 days of
                delivery for a full refund. We'll also pay the return shipping
                costs if the return is a result of our error (you received an
                incorrect or defective item, etc.).
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontFamily: '"Playfair Display", serif' }}>
                You should expect to receive your refund within four weeks of
                giving your package to the return shipper, however, in many
                cases you will receive a refund more quickly. This time period
                includes the transit time for us to receive your return from the
                shipper (5 to 10 business days), the time it takes us to process
                your return once we receive it (3 to 5 business days), and the
                time it takes your bank to process our refund request (5 to 10
                business days).
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontFamily: '"Playfair Display", serif' }}>
                If you need to return an item, simply login to your account,
                view the order using the 'Complete Orders' link under the My
                Account menu and click the Return Item(s) button. We'll notify
                you via e-mail of your refund once we've received and processed
                the returned item.
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                sx={{ mt: 3, fontFamily: '"Playfair Display", serif' }}
              >
                Shipping
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontFamily: '"Playfair Display", serif' }}>
                We can ship to virtually any address in the world. Note that
                there are restrictions on some products, and some products
                cannot be shipped to international destinations.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontFamily: '"Playfair Display", serif' }}>
                When you place an order, we will estimate shipping and delivery
                dates for you based on the availability of your items and the
                shipping options you choose. Depending on the shipping provider
                you choose, shipping date estimates may appear on the shipping
                quotes page.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontFamily: '"Playfair Display", serif' }}>
                Please also note that the shipping rates for many items we sell
                are weight-based. The weight of any such item can be found on
                its detail page. To reflect the policies of the shipping
                companies we use, all weights will be rounded up to the next
                full pound.
              </Typography>
            </ProductDetailAccordian>
          )}

          {product.customer_reviews && (
            <ProductDetailAccordian title={"Reviews"} sx={{ fontFamily: '"Playfair Display", serif' }}>
              <Typography variant="h3" fontWeight={500} gutterBottom mt={6} sx={{ fontFamily: '"Playfair Display", serif' }}>
                Customer Reviews
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body1" color="text.secondary" sx={{ fontFamily: '"Playfair Display", serif' }}>
                  Based on 1 review
                </Typography>
                <Link
                  href="#"
                  underline="none"
                  color="primary"
                  sx={{
                    fontWeight: 400,
                    color: "text.primary",
                    borderBottom: "2px dotted",
                    transition: "color 0.4s ease-in-out ",
                    "&:hover": {
                      color: "primary.main",
                    },
                    fontFamily: '"Playfair Display", serif'
                  }}
                >
                  Write a review
                </Link>
              </Box>
            </ProductDetailAccordian>
          )}
        </Box>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  ) : (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100vh"
    >
      <Typography variant="h6" color="error" sx={{ fontFamily: '"Playfair Display", serif' }}>
        Error loading product details. Please try again.
      </Typography>
    </Box>
  );
};

export default ProductDetailsPage;
